<template>
  <div class="flex flex-wrap">
    <div class="w-full px-4">
      <!-- Hero -->
      <div class="rounded-2xl shadow-xl bg-white overflow-hidden mb-8 border border-gray-100">
        <div class="bg-respina-primary px-6 py-8 text-white">
          <h1 class="text-2xl md:text-3xl font-bold mb-1">User Management</h1>
          <p class="text-respina-light text-sm opacity-90">
            Search users and manage admin access
          </p>
        </div>
      </div>

      <!-- Error -->
      <div
        v-if="error"
        class="mb-6 rounded-xl bg-red-50 border border-red-200 text-red-700 px-6 py-4 flex items-center justify-between"
      >
        <span><i class="fas fa-exclamation-circle ml-2"></i>{{ error }}</span>
        <button type="button" class="text-red-500 hover:text-red-700" @click="error = null">×</button>
      </div>

      <!-- Search -->
      <div class="rounded-2xl shadow-lg bg-white overflow-hidden border border-gray-100 mb-6">
        <div class="p-6">
          <label class="flex items-center gap-2 text-gray-700 text-sm font-semibold mb-3">
            <i class="fas fa-search text-respina-primary"></i>
            Search Users
          </label>
          <div class="flex gap-3">
            <input
              v-model="searchQuery"
              type="text"
              class="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-respina-primary focus:ring-2 focus:ring-blue-200 outline-none transition-all"
              placeholder="Search by email or name..."
              @keyup.enter="search"
            />
            <button
              type="button"
              @click="search"
              :disabled="searching"
              class="inline-flex items-center justify-center gap-2 px-6 h-12 bg-respina-primary hover:bg-respina-secondary text-white font-semibold text-sm rounded-xl shadow-md hover:shadow-lg transition-all duration-200 disabled:opacity-60"
            >
              <i class="fas text-sm" :class="searching ? 'fa-spinner fa-spin' : 'fa-search'"></i>
              <span>Search</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Results -->
      <div v-if="searched" class="rounded-2xl shadow-lg bg-white overflow-hidden border border-gray-100">
        <div v-if="!users.length" class="py-12 text-center text-gray-500">
          <i class="fas fa-user-slash text-3xl mb-3 text-gray-300"></i>
          <p>No users found.</p>
        </div>
        <div v-else class="divide-y divide-gray-100">
          <div
            v-for="u in users"
            :key="u.id"
            class="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors gap-4"
          >
            <div class="flex items-center gap-4 min-w-0">
              <div class="w-10 h-10 rounded-full bg-respina-light flex items-center justify-center shrink-0">
                <i class="fas fa-user text-respina-primary"></i>
              </div>
              <div class="min-w-0">
                <div class="font-semibold text-gray-800 truncate">{{ u.name || '(no name)' }}</div>
                <div class="text-sm text-gray-500 truncate">{{ u.email }}</div>
              </div>
              <span
                v-if="u.isAdmin"
                class="ml-1 px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 text-xs font-bold"
              >
                Admin
              </span>
              <span
                v-if="u.isSupport"
                class="ml-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 text-xs font-bold"
              >
                Support
              </span>
            </div>
            <div class="flex items-center gap-2 shrink-0">
              <button
                type="button"
                @click="toggleAdmin(u)"
                :disabled="u.togglingAdmin"
                class="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 disabled:opacity-60"
                :class="u.isAdmin
                  ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                  : 'bg-green-50 text-green-600 hover:bg-green-100 border border-green-200'"
              >
                <i class="fas" :class="u.togglingAdmin ? 'fa-spinner fa-spin' : (u.isAdmin ? 'fa-user-minus' : 'fa-user-shield')"></i>
                {{ u.isAdmin ? 'Remove Admin' : 'Make Admin' }}
              </button>
              <button
                type="button"
                @click="toggleSupport(u)"
                :disabled="u.togglingSupport"
                class="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 disabled:opacity-60"
                :class="u.isSupport
                  ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200'
                  : 'bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200'"
              >
                <i class="fas" :class="u.togglingSupport ? 'fa-spinner fa-spin' : (u.isSupport ? 'fa-user-minus' : 'fa-headset')"></i>
                {{ u.isSupport ? 'Remove Support' : 'Make Support' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiGet, apiPut } from "@/lib/http";

export default {
  data() {
    return {
      searchQuery: "",
      users: [],
      searching: false,
      searched: false,
      error: null,
    };
  },
  methods: {
    async search() {
      const q = this.searchQuery.trim();
      if (!q) return;
      this.searching = true;
      this.error = null;
      try {
        const data = await apiGet(`/api/users/search?q=${encodeURIComponent(q)}`);
        this.users = (data?.users ?? []).map(u => ({
          ...u,
          togglingAdmin: false,
          togglingSupport: false,
        }));
        this.searched = true;
      } catch (e) {
        this.error = e?.message ?? "Failed to search users";
      } finally {
        this.searching = false;
      }
    },
    async toggleAdmin(user) {
      user.togglingAdmin = true;
      this.error = null;
      try {
        const data = await apiPut(`/api/users/${user.id}/admin`, { isAdmin: !user.isAdmin });
        user.isAdmin = data?.user?.isAdmin ?? user.isAdmin;
        user.isSupport = data?.user?.isSupport ?? user.isSupport;
      } catch (e) {
        this.error = e?.message ?? "Failed to update user";
      } finally {
        user.togglingAdmin = false;
      }
    },
    async toggleSupport(user) {
      user.togglingSupport = true;
      this.error = null;
      try {
        const data = await apiPut(`/api/users/${user.id}/support`, { isSupport: !user.isSupport });
        user.isSupport = data?.user?.isSupport ?? user.isSupport;
        user.isAdmin = data?.user?.isAdmin ?? user.isAdmin;
      } catch (e) {
        this.error = e?.message ?? "Failed to update user";
      } finally {
        user.togglingSupport = false;
      }
    },
  },
};
</script>
